import i18n from 'helpers/i18n';
import messages from './messages';
import lock from 'images/lock.svg';
import RouteHelpers from 'helpers/routes';
import { useNavigate } from 'react-router-dom';
import warningReed from 'images/warningRed.svg';
import { CalibrationItem } from 'actions/calibrations';
import { TS_SUPPORT_URL } from 'constants/environmentVariables';
import { DateTime } from 'luxon';
import CalibrationTags from 'components/Calibrations/CalibrationTags';

interface CalibrationCardProps {
  calibration: CalibrationItem;
}

function CalibrationCard({ calibration }: CalibrationCardProps) {
  const navigate = useNavigate();

  function isExpired(status: string) {
    return status === 'expired';
  }

  function getButtonContent() {
    if (isExpired(calibration.status)) {
      return (
        <>
          {i18n.ft(messages.buttonExpired)}
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </>
      );
    }

    if (!isCalibrationAvailable(calibration.available_at)) {
      return (
        <>
          <img src={lock} alt="" className="inline" />
          {calibration.available_at ? (
            <span className="ml-2">
              {i18n.ft(messages.buttonAvailable, {
                date: calibration.available_at
              })}
            </span>
          ) : (
            i18n.ft(messages.buttonUnavailable)
          )}
        </>
      );
    }

    return (
      <>
        {i18n.ft(messages.buttonActive)}
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </>
    );
  }

  function handleButtonClick() {
    if (isExpired(calibration.status)) {
      window.open(TS_SUPPORT_URL, '_blank', 'noopener,noreferrer');
    } else {
      navigate(
        RouteHelpers.getPath('calibrations', {
          id: calibration.id
        })
      );
    }
  }

  function isCalibrationAvailable(availableAt?: string | null) {
    if (calibration.status === 'available') {
      if (!availableAt) {
        return true;
      }

      try {
        const availableDate: Date = new Date(availableAt);
        const currentDate: Date = new Date(Date.now());

        return availableDate?.getTime() <= currentDate?.getTime();
      } catch (_) {
        return false;
      }
    }

    if (calibration.status === 'expired') {
      return true;
    }

    return false;
  }

  const renderFormattedDate = (dateString: string) => {
    const dt = DateTime.fromFormat(dateString, 'yyyy-MM-dd HH:mm:ss z', {
      zone: 'utc'
    }).setZone(DateTime.local().zoneName);
    return dt.isValid ? `${dt.toFormat('MMMM d, yyyy h:mm a')}` : '';
  };

  return (
    <div
      key={calibration.id}
      className={`${
        isExpired(calibration.status) ? 'bg-[#FFF7F5]' : 'bg-white'
      } border rounded-lg p-4 md:p-6 shadow-sm hover:shadow-md transition-shadow`}
    >
      <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-start md:mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
            {calibration.name}
          </h3>
          <p
            className={`${
              isExpired(calibration.status)
                ? 'text-red-500 font-bold'
                : 'text-gray-600'
            } text-sm mb-3`}
          >
            {isExpired(calibration.status) ? (
              <>
                <img src={warningReed} alt="" className="inline" />{' '}
              </>
            ) : null}
            {` ${i18n.ft(messages.expires)} ${renderFormattedDate(calibration.expires_at)}`}
          </p>
          <CalibrationTags
            classVersion={calibration.test_template.class_version}
            ageLevelGroup={calibration.test_template.age_level_group}
          />
        </div>
        <div className="md:shrink-0">
          <button
            className={`${
              isCalibrationAvailable(calibration.available_at)
                ? 'bg-[#61810D] text-white'
                : 'bg-[#E2E2E2] text-[#7E7D85]'
            } w-full justify-center md:w-auto px-6 py-2 rounded-full font-bold transition-colors flex items-center gap-2`}
            onClick={handleButtonClick}
            disabled={!isCalibrationAvailable(calibration.available_at)}
          >
            {getButtonContent()}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CalibrationCard;
